/**
 * Context route — OpenClaw workspace context file editor.
 * Features: CodeMirror 6, auto-save, file grouping, create new memory files.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText,
  Save,
  Check,
  AlertTriangle,
  Plus,
  X,
  FolderOpen,
  Settings,
  Brain,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { GlyphButton } from "../components/ui/GlyphButton";
import { StatusDot } from "../components/ui/StatusDot";
import { CodeMirrorEditor } from "../components/editor/CodeMirrorEditor";

interface ContextFile {
  name: string;
  path: string;
  exists: boolean;
  size: number;
  modifiedAt: number;
}

interface FilesResponse {
  files: ContextFile[];
}

interface FileContentResponse {
  name: string;
  path: string;
  content: string;
  size: number;
  modifiedAt: number;
  error?: string;
}

interface SaveResponse {
  success?: boolean;
  error?: string;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

function getLanguageFromFilename(
  filename: string
): "markdown" | "json" | "javascript" | "plain" {
  if (filename.endsWith(".md")) return "markdown";
  if (filename.endsWith(".json")) return "json";
  if (filename.endsWith(".js") || filename.endsWith(".ts")) return "javascript";
  return "plain";
}

function getFileIcon(filename: string) {
  if (filename.startsWith("memory/")) return Brain;
  if (
    filename === "TOOLS.md" ||
    filename === "AGENTS.md" ||
    filename === "BOOTSTRAP.md"
  )
    return Settings;
  return FileText;
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

// Validate memory file name: memory/YYYY-MM-DD.md
function isValidMemoryFileName(name: string): boolean {
  const pattern = /^memory\/\d{4}-\d{2}-\d{2}\.md$/;
  return pattern.test(name);
}

export function ContextRoute() {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [content, setContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [cursorPos, setCursorPos] = useState({ line: 1, col: 1 });
  const [showNewFileModal, setShowNewFileModal] = useState(false);
  const [newFileName, setNewFileName] = useState("");
  const [newFileError, setNewFileError] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    { root: true, memory: true }
  );
  const [mobileFileListOpen, setMobileFileListOpen] = useState(false);

  // Debounce timer ref
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch file list
  const { data: filesData, isLoading: loadingFiles } = useQuery({
    queryKey: ["context-files"],
    queryFn: async () => {
      const res = await fetch("/api/context/files");
      return res.json() as Promise<FilesResponse>;
    },
  });

  // Fetch file content
  const {
    data: fileData,
    isLoading: loadingContent,
    refetch: refetchContent,
  } = useQuery({
    queryKey: ["context-file", selectedFile],
    queryFn: async () => {
      if (!selectedFile) return null;
      const res = await fetch(
        `/api/context/file?name=${encodeURIComponent(selectedFile)}`
      );
      return res.json() as Promise<FileContentResponse>;
    },
    enabled: !!selectedFile,
  });

  // Update content when file loads
  useEffect(() => {
    if (fileData?.content !== undefined) {
      setContent(fileData.content);
      setOriginalContent(fileData.content);
      setIsDirty(false);
      setSaveStatus("idle");
    }
  }, [fileData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile) throw new Error("No file selected");
      const res = await fetch(
        `/api/context/file?name=${encodeURIComponent(selectedFile)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content }),
        }
      );
      return res.json() as Promise<SaveResponse>;
    },
    onMutate: () => {
      setSaveStatus("saving");
    },
    onSuccess: (data) => {
      if (data.error) {
        setSaveStatus("error");
      } else {
        setSaveStatus("saved");
        setIsDirty(false);
        setOriginalContent(content);
        setLastSaved(new Date());
        refetchContent();
        queryClient.invalidateQueries({ queryKey: ["context-files"] });
        // Reset status after 3 seconds
        setTimeout(() => setSaveStatus("idle"), 3000);
      }
    },
    onError: () => {
      setSaveStatus("error");
    },
  });

  // Create file mutation
  const createFileMutation = useMutation({
    mutationFn: async (filename: string) => {
      const res = await fetch(
        `/api/context/file?name=${encodeURIComponent(filename)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: `# ${filename}\n\n` }),
        }
      );
      return res.json() as Promise<SaveResponse>;
    },
    onSuccess: (data, filename) => {
      if (!data.error) {
        queryClient.invalidateQueries({ queryKey: ["context-files"] });
        setShowNewFileModal(false);
        setNewFileName("");
        setSelectedFile(filename);
      } else {
        setNewFileError(data.error);
      }
    },
    onError: (err) => {
      setNewFileError(err instanceof Error ? err.message : "Error creating file");
    },
  });

  const handleContentChange = useCallback((newContent: string) => {
    setContent(newContent);
    setIsDirty(newContent !== originalContent);
    setSaveStatus("idle");

    // Clear existing auto-save timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new auto-save timer (2 seconds debounce)
    autoSaveTimerRef.current = setTimeout(() => {
      if (newContent !== originalContent) {
        saveMutation.mutate();
      }
    }, 2000);
  }, [originalContent, saveMutation]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  const handleSave = useCallback(() => {
    // Clear auto-save timer since we're saving manually
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    if (isDirty && selectedFile) {
      saveMutation.mutate();
    }
  }, [isDirty, selectedFile, saveMutation]);

  const handleCursorChange = useCallback((line: number, col: number) => {
    setCursorPos({ line, col });
  }, []);

  const handleCreateFile = () => {
    setNewFileError("");

    // Auto-format: if user just types date, prepend memory/ and append .md
    let filename = newFileName.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(filename)) {
      filename = `memory/${filename}.md`;
    }

    if (!isValidMemoryFileName(filename)) {
      setNewFileError("Formato: memory/YYYY-MM-DD.md");
      return;
    }

    createFileMutation.mutate(filename);
  };

  const handleFileSelect = (filename: string) => {
    if (isDirty && selectedFile !== filename) {
      // Auto-save before switching
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      saveMutation.mutate();
    }
    setSelectedFile(filename);
    setMobileFileListOpen(false);
  };

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  // Group files
  const { rootFiles, memoryFiles } = useMemo(() => {
    const files = filesData?.files ?? [];
    const existing = files.filter((f) => f.exists);

    const root: ContextFile[] = [];
    const memory: ContextFile[] = [];

    for (const file of existing) {
      if (file.name.startsWith("memory/")) {
        memory.push(file);
      } else {
        root.push(file);
      }
    }

    // Sort memory files by date (newest first)
    memory.sort((a, b) => b.name.localeCompare(a.name));

    return { rootFiles: root, memoryFiles: memory };
  }, [filesData]);

  const language = selectedFile ? getLanguageFromFilename(selectedFile) : "plain";
  const wordCount = countWords(content);
  const charCount = content.length;

  // Dirty files tracking for visual indicator
  const dirtyFiles = useMemo(() => {
    const set = new Set<string>();
    if (isDirty && selectedFile) {
      set.add(selectedFile);
    }
    return set;
  }, [isDirty, selectedFile]);

  const renderFileItem = (file: ContextFile) => {
    const Icon = getFileIcon(file.name);
    const isSelected = file.name === selectedFile;
    const isFileDirty = dirtyFiles.has(file.name);

    return (
      <div
        key={file.name}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-sm cursor-pointer
          transition-colors relative
          ${
            isSelected
              ? "bg-[var(--bw-accent)]/10 border-l-2 border-l-[var(--bw-accent)]"
              : "hover:bg-[var(--bw-surface-2)]"
          }
        `}
        onClick={() => handleFileSelect(file.name)}
      >
        <Icon size={14} className="text-[var(--bw-muted)] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[var(--bw-fg)] truncate font-mono">
            {file.name.replace("memory/", "")}
          </p>
          <p className="text-xs text-[var(--bw-muted)]">
            {formatSize(file.size)}
          </p>
        </div>
        {isFileDirty && (
          <span
            className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[var(--bw-warn)]"
            title="Sin guardar"
          />
        )}
      </div>
    );
  };

  const renderFileGroup = (
    title: string,
    groupKey: string,
    files: ContextFile[],
    icon: React.ReactNode
  ) => {
    const isExpanded = expandedGroups[groupKey] ?? true;

    return (
      <div className="mb-2">
        <div
          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[var(--bw-surface-2)] rounded-sm"
          onClick={() => toggleGroup(groupKey)}
        >
          {isExpanded ? (
            <ChevronDown size={12} className="text-[var(--bw-muted)]" />
          ) : (
            <ChevronRight size={12} className="text-[var(--bw-muted)]" />
          )}
          {icon}
          <span className="text-xs text-[var(--bw-muted)] uppercase tracking-wider flex-1">
            {title}
          </span>
          <span className="text-xs text-[var(--bw-muted)]">{files.length}</span>
        </div>
        {isExpanded && (
          <div className="ml-2">{files.map((f) => renderFileItem(f))}</div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col md:flex-row">
      {/* Mobile file list toggle */}
      <div className="md:hidden p-2 border-b border-[var(--bw-border)] bg-[var(--bw-surface)]">
        <button
          onClick={() => setMobileFileListOpen(!mobileFileListOpen)}
          className="flex items-center gap-2 text-sm text-[var(--bw-fg)] w-full px-3 py-2"
        >
          <FolderOpen size={14} />
          <span>{selectedFile ?? "Seleccionar archivo"}</span>
          {mobileFileListOpen ? (
            <ChevronDown size={14} className="ml-auto" />
          ) : (
            <ChevronRight size={14} className="ml-auto" />
          )}
        </button>
      </div>

      {/* File list sidebar */}
      <div
        className={`
          w-full md:w-64 md:border-r border-b md:border-b-0 border-[var(--bw-border)]
          flex flex-col bg-[var(--bw-surface)]
          ${mobileFileListOpen ? "max-h-60" : "max-h-0 md:max-h-none"}
          md:max-h-none overflow-hidden transition-all duration-200
        `}
      >
        <div className="hidden md:flex p-3 border-b border-[var(--bw-border)] items-center justify-between">
          <CRTHeader text="Context Files" level={3} />
          <button
            onClick={() => setShowNewFileModal(true)}
            className="p-1.5 rounded-sm hover:bg-[var(--bw-surface-2)] text-[var(--bw-muted)] hover:text-[var(--bw-accent)] transition-colors"
            title="Crear archivo de memoria"
          >
            <Plus size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {loadingFiles && (
            <p className="text-sm text-[var(--bw-muted)] animate-pulse p-2">
              CARGANDO...
            </p>
          )}

          {!loadingFiles && (
            <>
              {renderFileGroup(
                "Archivos Base",
                "root",
                rootFiles,
                <FileText size={12} className="text-[var(--bw-accent)]" />
              )}
              {renderFileGroup(
                "Memory",
                "memory",
                memoryFiles,
                <Brain size={12} className="text-[var(--bw-warn)]" />
              )}
            </>
          )}

          {!loadingFiles &&
            rootFiles.length === 0 &&
            memoryFiles.length === 0 && (
              <p className="text-sm text-[var(--bw-muted)] text-center py-4">
                Sin archivos
              </p>
            )}
        </div>

        {/* Mobile create button */}
        <div className="md:hidden p-2 border-t border-[var(--bw-border)]">
          <GlyphButton
            onClick={() => setShowNewFileModal(true)}
            icon={<Plus size={14} />}
            className="w-full justify-center"
          >
            Nuevo archivo
          </GlyphButton>
        </div>
      </div>

      {/* Editor panel */}
      <div className="flex-1 flex flex-col min-h-0">
        {!selectedFile ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <CorpoCard>
              <div className="text-center py-8 px-6">
                <FileText
                  size={48}
                  className="text-[var(--bw-muted)] mx-auto mb-4"
                />
                <p className="text-[var(--bw-muted)] uppercase tracking-wider">
                  SELECCIONA UN ARCHIVO
                </p>
                <p className="text-xs text-[var(--bw-muted)] mt-1">
                  Edita archivos de contexto de OpenClaw
                </p>
              </div>
            </CorpoCard>
          </div>
        ) : (
          <>
            {/* Editor header */}
            <div className="p-3 border-b border-[var(--bw-border)] flex flex-wrap items-center justify-between gap-2 bg-[var(--bw-surface)]">
              <div className="flex items-center gap-3 min-w-0">
                <CRTHeader text={selectedFile} level={2} className="truncate" />
                <span className="px-1.5 py-0.5 text-xs rounded bg-[var(--bw-surface-2)] text-[var(--bw-muted)] uppercase">
                  {language}
                </span>
                {isDirty && (
                  <span className="text-xs text-[var(--bw-warn)] uppercase">
                    ●
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                {saveStatus === "saving" && (
                  <span className="text-xs text-[var(--bw-muted)] animate-pulse">
                    Guardando...
                  </span>
                )}
                {saveStatus === "saved" && lastSaved && (
                  <StatusDot
                    status="online"
                    size="sm"
                    label={`Guardado ${formatTime(lastSaved)}`}
                  />
                )}
                {saveStatus === "error" && (
                  <div className="flex items-center gap-2">
                    <StatusDot status="error" size="sm" label="Error" />
                    <button
                      onClick={handleSave}
                      className="text-xs text-[var(--bw-danger)] underline"
                    >
                      Reintentar
                    </button>
                  </div>
                )}

                <GlyphButton
                  onClick={handleSave}
                  disabled={!isDirty || saveStatus === "saving"}
                  icon={
                    saveStatus === "saved" ? (
                      <Check size={14} />
                    ) : (
                      <Save size={14} />
                    )
                  }
                >
                  Guardar
                </GlyphButton>
              </div>
            </div>

            {/* Stats bar */}
            <div className="px-3 py-1.5 border-b border-[var(--bw-border)] bg-[var(--bw-surface-2)] flex items-center gap-4 text-xs text-[var(--bw-muted)]">
              <span>{wordCount} palabras</span>
              <span>{charCount} caracteres</span>
              <span>{formatSize(charCount)}</span>
            </div>

            {/* Editor content */}
            <div className="flex-1 min-h-0 overflow-hidden">
              {loadingContent ? (
                <div className="p-4 text-[var(--bw-muted)] animate-pulse">
                  CARGANDO ARCHIVO...
                </div>
              ) : fileData?.error ? (
                <div className="p-4">
                  <CorpoCard className="border-[var(--bw-danger)]">
                    <div className="flex items-center gap-2 text-[var(--bw-danger)]">
                      <AlertTriangle size={16} />
                      <span>{fileData.error}</span>
                    </div>
                  </CorpoCard>
                </div>
              ) : (
                <CodeMirrorEditor
                  value={content}
                  onChange={handleContentChange}
                  language={language}
                  height="100%"
                  onSave={handleSave}
                  onCursorChange={handleCursorChange}
                />
              )}
            </div>

            {/* Bottom bar */}
            <div className="px-3 py-1.5 border-t border-[var(--bw-border)] bg-[var(--bw-surface)] flex items-center justify-between text-xs text-[var(--bw-muted)]">
              <span>
                Ln {cursorPos.line}, Col {cursorPos.col}
              </span>
              <div className="flex items-center gap-4">
                <span>⌘S guardar</span>
                <span>⌃F buscar</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* New file modal */}
      {showNewFileModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bw-surface)] border border-[var(--bw-border)] rounded-sm p-4 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[var(--bw-fg)] font-mono uppercase">
                Nuevo archivo de memoria
              </h3>
              <button
                onClick={() => {
                  setShowNewFileModal(false);
                  setNewFileName("");
                  setNewFileError("");
                }}
                className="text-[var(--bw-muted)] hover:text-[var(--bw-fg)]"
              >
                <X size={16} />
              </button>
            </div>

            <p className="text-xs text-[var(--bw-muted)] mb-3">
              Formato: memory/YYYY-MM-DD.md o solo YYYY-MM-DD
            </p>

            <input
              type="text"
              value={newFileName}
              onChange={(e) => {
                setNewFileName(e.target.value);
                setNewFileError("");
              }}
              placeholder={`${new Date().toISOString().split("T")[0]}`}
              className="w-full bg-[var(--bw-bg)] border border-[var(--bw-border)] text-[var(--bw-fg)] px-3 py-2 font-mono text-sm rounded-sm focus:border-[var(--bw-accent)] focus:outline-none"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateFile();
                if (e.key === "Escape") {
                  setShowNewFileModal(false);
                  setNewFileName("");
                  setNewFileError("");
                }
              }}
              autoFocus
            />

            {newFileError && (
              <p className="text-xs text-[var(--bw-danger)] mt-2">
                {newFileError}
              </p>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <GlyphButton
                onClick={() => {
                  setShowNewFileModal(false);
                  setNewFileName("");
                  setNewFileError("");
                }}
              >
                Cancelar
              </GlyphButton>
              <GlyphButton
                onClick={handleCreateFile}
                disabled={createFileMutation.isPending}
                icon={<Plus size={14} />}
              >
                {createFileMutation.isPending ? "Creando..." : "Crear"}
              </GlyphButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
